//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === COMPONENTS ===
import { Document } from './components/Document';

// === API ===
import {emit, on, removeListener, sendMessageData} from 'api/prokey/prokey.websocket';

//#endregion

const DocMemo = React.memo( Document );

//-----------------------------------------------------------------
export function Thumbnails( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { fill } = props;

  const theme = useTheme();
  
  const [ documents, setDocuments ] = useState( { items: {}, order: [] } );
  const [    images, setImages    ] = useState( { items: {}, order: [] } );
  
  //#endregion

  //#region [STYLES]

  const _styles = {
    thumb_paper: {
      gridColumn: 'span ' + fill || 2,
      overflow : 'auto'     ,
      position : 'relative' ,
      height   : '100%',
      width    : '100%',
      boxShadow: 0,
      backgroundColor: theme.palette.background.secondary[theme.palette.mode],
    },
    document_container: {
      height: '100%', width: '100%', overflow: 'auto',
    }
  }

  //#endregion
  
  //#region [EFFECT]

  useEffect( () => {

    //-----------------------------------------------------------------
    function handleDocuments(data) {
      //-----------------------------------------------------------------
      setDocuments( data );
    }

    //-----------------------------------------------------------------
    function handleImages(data) {
      //-----------------------------------------------------------------
      setImages( data );
    }

    //-----------------------------------------------------------------
    function handleImageUpdates( data ) {
      //-----------------------------------------------------------------
      const temp = { ...images };
      if( data.images?.order ) temp.order = data.images.order;
      
      data.images?.update?.forEach( image => {
        emit( 'IMAGE-'+ image.id, image );
      });
    }

    //-----------------------------------------------------------------
    function handleClear(data) {
      //-----------------------------------------------------------------
      setImages   ( { items: {}, order: [] } );
      setDocuments( { items: {}, order: [] } );
    }

    on( 'TASK_DOCUMENTS' , handleDocuments    );
    on( 'TASK_IMAGES'    , handleImages       );
    on( 'IMAGE_UPDATE'   , handleImageUpdates );
    on( 'THUMBS_CLEAR'   , handleClear        );

    return () => {
      removeListener( 'TASK_DOCUMENTS' , handleDocuments    );
      removeListener( 'TASK_IMAGES'    , handleImages       );
      removeListener( 'IMAGE_UPDATE'   , handleImageUpdates );
      removeListener( 'THUMBS_CLEAR'   , handleClear        );
    }

  }, [] );
      
  useEffect( () => {
    //-----------------------------------------------------------------
    function unsubscribe() {
      //-----------------------------------------------------------------
      sendMessageData( 'TASK_VIEW_THUMBS_UNSUBSCRIBE' );
    }

    sendMessageData( 'TASK_VIEW_THUMBS_SUBSCRIBING' );

    window.addEventListener('beforeunload', unsubscribe );
    return () => {
      window.removeEventListener('beforeunload', unsubscribe );
    }
  }, [] );
  
  useEffect( () => {
    function _handleReconnect(data) {
      sendMessageData( 'TASK_VIEW_THUMBS_SUBSCRIBING', data );
    }

    on( 'connection', _handleReconnect );

    return () => {
      removeListener( 'connection', _handleReconnect );
    }
  }, [] );
  
  useEffect( () => {
    //-----------------------------------------------------------------
    function handleDocumentUpdates( data ) {
      //-----------------------------------------------------------------
      try {
        const temp = { ...documents };

        // === DOCUMENTS
        if( data.documents?.order ) temp.order = data.documents.order;

        data.documents?.create?.forEach( document => {
          temp.items[ document.Id ] = document;
        });
        data.documents?.update?.forEach( document => {
          document.timestamp = Date.now();
          temp.items[ document.Id ] = document;
        });
        data.documents?.remove?.forEach( id => {   
          delete temp.items[ id ];
        });
        setDocuments( temp );
      } catch (error) {
        console.warn( error );
      }
    }

    on( 'DOCUMENT_UPDATE' , handleDocumentUpdates );

    return () => {
      removeListener( 'DOCUMENT_UPDATE' , handleDocumentUpdates );
    }
  }, [documents] );

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const thumb_documents = documents.order.map( displayDocument );
    
    return (
      <Box sx={_styles.thumb_paper}>
        <Box sx={_styles.document_container} >
          { thumb_documents }
        </Box>
      </Box>
    );
  }
  
  //-----------------------------------------------------------------
  function displayDocument( id, index ) {
    //-----------------------------------------------------------------
    const document = documents.items[id];    
    if( !document ) {
      console.log( `Failed to get document with id [${id}].`);
    }
    const timestamp = document?.timestamp;    
    return (
      <Box key={ 'd' + id }>
        <DocMemo key={ 'd' + id } id={ id } timestamp={timestamp} index={index} images={images.order} />       
      </Box>  
      );
    }

  //#endregion

  return display();
}
